<template>
  <!--
    Video Block that displays a thumbnail image, title and description.
    When the thumbnail image or "watch now" is clicked an iframe video will expand,
    hiding the description and showing a collapsible transcript.
  -->
  <div
    class="video-block"
    :class="{'video-block--expanded': showVideo}"
    role="article"
    aria-label="Video block"
  >
    <!-- Thumbnail Image -->
    <button
      v-show="!showVideo"
      title="watch now"
      class="video-block__thumbnail"
      @click="toggleVideo"
      @keyup.space="toggleVideo"
    >
      <div class="video-block__thumbnail-container">
        <div class="video-block__thumbnail-wrapper">
          <slot
            name="thumbnail"
            class="video-block__thumbnail-image"
          />
          <div class="video-block__thumbnail-icon-wrapper">
            <svg
              class="svg-inline--fa fa-play fa-w-14 video-block__thumbnail-icon"
              aria-hidden="true"
              focusable="false"
              data-prefix="fas"
              data-icon="play"
              role="img"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 448 512"
              data-v-31b9e27e=""
            ><path
              class=""
              fill="currentColor"
              d="M424.4 214.7L72.4 6.6C43.8-10.3 0 6.1 0 47.9V464c0 37.5 40.7 60.1 72.4 41.3l352-208c31.4-18.5 31.5-64.1 0-82.6z"
            /></svg>
          </div>
        </div>
      </div>
    </button>
    <!-- Video -->
    <div
      v-show="showVideo"
      class="video-block__player"
    >
      <div class="video-block__player-wrapper">
        <pulse-loader
          :color="'#E4E6E2'"
          :size="'20px'"
          :margin="'10px'"
        />
        <iframe
          v-if="showVideo"
          :src="computedUrl"
          :title="title"
          class="video-block__iframe"
          frameborder="0"
          allow="autoplay; fullscreen"
          allowfullscreen
        />
      </div>
      <!-- Transcript -->
      <div class="video-block__transcript">
        <button
          :id="`transcript-button-${videoID}`"
          class="video-block__transcript-button"
          :class="{
            'video-block__transcript-button--expanded': showTranscript,
          }"
          :aria-controls="`transcript-content-${videoID}`"
          :aria-label="`Video transcript`"
          :aria-expanded="showTranscript"
          @click="toggleTranscript"
          @keyup.space="toggleTranscript"
        >
          <span class="video-block__transcript-label">
            <img
              loading="lazy"
              class="video-block__transcript-icon"
              :src="`/themes/custom/education_base/assets/images/icon-transcript.svg`"
              alt=""
            >
            <span> Video transcript </span>
          </span>
          <img
            class="video-block__transcript-close-btn"
            :src="`/themes/custom/education_base/assets/images/icon-close.svg`"
            alt="Close transcript icon"
          >
        </button>
        <div
          :id="`transcript-content-${videoID}`"
          class="video-block__transcript-content"
          :class="{
            'video-block__transcript-content--expanded': showTranscript,
          }"
          :aria-labelledby="`transcript-button-${videoID}`"
          :aria-hidden="!showTranscript"
        >
          <div
            class="video-block__transcript-content-inner"
            :class="{
              'video-block__transcript-content-inner--expanded': showTranscript,
            }"
          >
            <slot name="transcript" />
          </div>
        </div>
      </div>
    </div>
    <!-- Title & Description -->
    <div
      class="video-block__content"
      :class="{'video-block__content--expanded': showVideo}"
    >
      <div class="video-block__title-wrapper">
        <h3
          class="video-block__title"
          :class="{'video-block__title--expanded': showVideo}"
          @click="toggleVideo"
          @keyup.space="toggleVideo"
        >
          {{ title }}
        </h3>
      </div>
      <button
        v-if="showVideo && !showLargeOnly"
        class="video-block__close c-button c-button--link"
        @click="toggleVideo"
        @keyup.space="toggleVideo"
      >
        <img
          :src="`/themes/custom/education_base/assets/images/icon-close.svg`"
          alt="Close video icon"
        >
      </button>
      <div
        v-if="!showVideo"
        class="video-block__description"
      >
        {{ description }}
      </div>
      <div class="video-block__content-footer">
        <button
          v-if="!showVideo"
          class="video-block__watch-button c-button c-button--link"
          @click="toggleVideo"
          @keyup.space="toggleVideo"
        >
          WATCH NOW
        </button>
        <span
          role="text"
          :aria-label="`Video is ${duration} long`"
          class="video-block__duration">
          {{ duration }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { PulseLoader } from 'vue-spinner/dist/vue-spinner.min';

export default {
  name: 'VideoBlock',
  components: {
    PulseLoader,
  },
  props: {
    /**
     * Title for the block
     */
    title: {
      type: String,
      required: true,
    },
    /**
     * Description for the block
     */
    description: {
      type: String,
      required: true,
    },
    /**
     * Video duration
     */
    duration: {
      type: String,
      required: true,
    },
    /**
     * If true, only the large expanded view is displayed
     */
    isLargeView: {
      type: String,
      required: false,
      default: 'Off',
    },
    /**
     * Video image url from drupal. Needs to be parsed to extract ID
     */
    vimeoVideo: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      showLargeOnly: this.isLargeView === 'On',
      showVideo: false,
      showTranscript: false,
      videoID: 0,
    };
  },
  computed: {
    /**
     * Do not autoplay video when large view only is enabled
     */
    computedUrl() {
      return this.showLargeOnly ? this.videoUrl : `${this.videoUrl}?autoplay=1`;
    },
    videoUrl() {
      return `https://player.vimeo.com/video/${this.videoID}`;
    },
  },
  async mounted() {
    this.showVideo = this.showLargeOnly;
    const thumbnailImage = /[^/]*$/.exec(this.vimeoVideo)[0];
    this.videoID = thumbnailImage.split('.')[0];
  },

  methods: {
    /**
     * Used for hiding and showing the video iframe
     */
    toggleVideo() {
      if (this.showLargeOnly) return;
      this.showVideo = !this.showVideo;
    },
    /**
     * Used for expanding and collapsing the video transcript
     */
    toggleTranscript() {
      this.showTranscript = !this.showTranscript;
    },
  },
};
</script>

<style scoped lang="scss">
/* stylelint-disable-next-line unit-disallowed-list */
$size-thumbnail-width: 347px;
/* stylelint-disable-next-line unit-disallowed-list */
$size-thumbnail-height: 196px;
/* stylelint-disable-next-line unit-disallowed-list */
$size-video-height: 459px;
/* stylelint-disable-next-line unit-disallowed-list */
$size-video-width: 816px;

@mixin focus-outline {
  outline: $border-width-bold solid $color-blue;
  outline-offset: $border-width-bold;
}

@mixin reset-button() {
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
}

.video-block {
  display: flex;
  justify-content: space-between;
  transition: all 200ms $transition-timing;
  margin: auto;
  border-radius: 5px;

  @include education-breakpoint-mobile-down {
    flex-direction: column;
  }

  &--expanded {
    flex-direction: column-reverse;
    border-radius: 5px;
    background: $color-white;
  }

  &__thumbnail {
    @include reset-button();

    width: $size-thumbnail-width;
    margin-right: gap-size(1.22);
    position: relative;
    transition: opacity $transition-duration $transition-timing;

    @include hover-focus-active {
      opacity: 0.8;
    }

    @include focus-active {
      @include focus-outline;
    }

    @include education-breakpoint-mobile-down {
      width: 100%;
      margin-right: 0;
      margin-bottom: gap-size(0.8);
    }
  }

  &__thumbnail-container {
    position: relative;
    width: $size-thumbnail-width;
    height: $size-thumbnail-height;

    @include education-breakpoint-mobile-down {
      aspect-ratio: calc($size-thumbnail-width / $size-thumbnail-height);
      width: 100%;
      height: 100%;
    }
  }

  &__thumbnail-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
  }

  &__thumbnail-icon-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    right: 0;
    bottom: 0;
    height: 2.2rem;
    width: 2.2rem;
  }

  &__thumbnail-icon {
    width: 100%;
    height: 100%;
    background-color: $color-blue;
    padding: gap-size(1*0.33);
    font-size: $font-size-xsmall;
    color: $color-white;
  }

  &__player {
    width: 100%;
    position: relative;
  }

  &__player-wrapper {
    position: relative;
    width: 100%;
    aspect-ratio: 16/9;
    overflow: hidden;
    background: $color-light-grey;

    @include education-breakpoint-mobile-down() {
      width: 100%;
    }
  }

  .v-spinner {
    position: relative;
    top: calc(50% - 1rem);

    .v-pulse {
      margin: gap-size(0.25);
    }
  }

  &__iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }

  &__close {
    display: flex;
    justify-content: flex-start;
    transform: rotate(45deg);

    @include focus-active {
      @include focus-outline;
    }
  }

  &__content {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    margin: 0;

    &--expanded {
      flex-wrap: initial;
      padding: gap-size(0.65) gap-size(0.7);
      box-sizing: border-box;
      border: $border-width solid $color-grey;
      border-radius: 5px 5px 0 0;
    }
  }

  &__content-footer {
    display: flex;
    justify-content: space-between;
    width: 100%;

    .video-block--expanded & {
      display: none;
    }
  }

  &__watch-button {
    font-weight:bold;
  }

  &__duration {
    @include heading-x-small;
  }

  &__title {
    @include heading-small-medium;

    display: inline-block;
    margin: gap-size(0.21) 0 gap-size(0.5) 0;

    &:hover {
      cursor: pointer;
    }

    @include focus-active {
      @include focus-outline;
    }

    @include education-breakpoint-mobile-down() {
      margin: 0 0 gap-size(0.5) 0;
      // TODO: double check
      font-weight: 700;
    }

    &:not(:first-child) {
      margin-top: 0;
    }

    &--expanded {
      display: block;
      width: 100%;
      box-sizing: border-box;
      margin: 0;
      pointer-events: none;
    }
  }

  &__title-wrapper {
    width: 100%;
  }

  &__description {
    width: 100%;

    @include text-small;

    margin: 0 0 gap-size(1.39) 0;

    @include education-breakpoint-mobile-down {
      margin-bottom: gap-size(0.67);
    }
  }

  &__underline {
    width: gap-size(1);
    height: 0.3rem;
    background-color: $color-yellow;
  }

  &__transcript {
    width: 100%;
    box-sizing: border-box;
    border: $border-width solid $color-grey;
    background: $color-white;
    border-radius: 0 0 5px 5px;
  }

  &__transcript-button {
    @include reset-button();

    position: relative;
    width: 100%;
    padding: gap-size(0.5) gap-size(0.7);
    box-sizing: border-box;
    height: 3.72rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    @include text-small;

    &--expanded {
      .video-block__transcript-close-btn {
        transform: rotate(45deg);
      }
      // color: $color-blue;
    }

    &:hover {
      color: $color-blue;

      .video-block__transcript-close-btn {
        content: url("/web/themes/custom/education_base/assets/images/icon-close-blue.svg");
      }

      .video-block__transcript-icon {
        content: url("/web/themes/custom/education_base/assets/images/icon-transcript-blue.svg");
      }
    }

    @include focus-active {
      @include focus-outline;
    }
  }

  &__transcript-close-btn {
    transition: transform $transition-duration $transition-timing;
  }

  &__transcript-label {
    display: flex;

    @include text-small;
  }

  &__transcript-icon {
    margin-right: gap-size(0.55);
  }

  &__transcript-content {
    @include text-small;

    height: 0;
    overflow: hidden;
    transition: height $transition-duration $transition-timing;

    &--expanded {
      height: auto;
      margin-bottom: 1rem;
      padding: gap-size(0.5) gap-size(1.67);

      @include education-breakpoint-mobile-down {
        padding: 0 gap-size(0.7);
      }

      .video-block__transcript-content-inner {
        transform: translate3d(0, 0, 0);
        opacity: 1;
      }
    }
  }

  &__transcript-content-inner {
    transition: $transition-expand;
    transform: translate3d(0, -1rem, 0);
    opacity: 0;
  }
}
</style>
<style lang="scss">
.video-block {
  &__thumbnail-wrapper img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>
